'use client';

import { Listing } from '@engelvoelkersamericas/api';
import { Trash2 } from '@icon/Trash2';
import { CircularProgress, ClickAwayListener } from '@mui/material';
import {
  DrawingManager,
  GoogleMap,
  InfoBox,
  Marker,
  MarkerClusterer,
  Polygon,
  useJsApiLoader,
} from '@react-google-maps/api';
import { useCurrency } from '@util/customHooks/useCurrency';
import useIsMobile from '@util/customHooks/useIsMobile';
import { GMAPS_LIBRARIES } from '@util/customHooks/useLocationToGeoCode';

import { Center } from '@components/PropertiesSearch';
import { debounce } from 'lodash';
import { useTranslations } from 'next-intl';
import {
  Dispatch,
  FC,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import ElasticListingMarker from './PropertyListingMarker';
import {
  DeleteIconContainer,
  DrawAreaButton,
  Headline,
  InfoBoxDescription,
  MapWrapper,
  OverlayLoader,
  StyledInfoBoxWrapper,
  StyledWrapper,
} from './styles';
import { LatLng } from '@components/PageLevelComponents/Shops/NeighborhoodHomepage';

export interface PropertyListingsMapProps {
  listings: Listing[];
  config: any;
  zoomValue: number;
  setZoomValue?: Dispatch<SetStateAction<string | null>>;
  setCenter?: Dispatch<SetStateAction<Center | null>>;
  setCenterAndZoom?: boolean;
  mapPropertyLoader?: boolean;
  isMarkerHover?: boolean;
  onPolygonChange?: (polygon: { lat: number; lng: number }[] | null) => void;
  center?: Center | null;
  kmlPolygon?: LatLng[];
  userPolygon?: LatLng[] | null;
  isNeighborhoodPage?: boolean;
}

const PropertyListingsMap: FC<PropertyListingsMapProps> = ({
  listings,
  config,
  zoomValue,
  setCenterAndZoom = true,
  mapPropertyLoader = false,
  isMarkerHover = false,
  setZoomValue = () => {},
  setCenter = () => {},
  center,
  onPolygonChange = () => {},
  kmlPolygon,
  userPolygon,
  isNeighborhoodPage = false,
}) => {
  const t = useTranslations();
  const mapRef = useRef<google.maps.Map | null>(null);
  const [activeMarkerPosition, setActiveMarkerPosition] = useState(null);
  const [activeMarker, setActiveMarker] = useState<Listing | null>(null);
  const [activeCluster, setActiveCluster] = useState<{
    count: number;
    min: number;
    max: number;
    average: number;
  } | null>(null);

  const polygonCreatedRef = useRef(false);
  const [polygonRef, setPolygonRef] = useState<google.maps.Polygon | null>(null);
  const [isFirstPageLoad, setIsFirstPageLoad] = useState(true);
  const [clickedMarkerInfo, setClickedMarkerInfo] = useState<{
    e: Event;
    activeMarkerData: Listing;
  } | null>(null);
  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: config.google.mapKey,
    libraries: GMAPS_LIBRARIES,
  });

  const { getListingPrice, formatCurrency } = useCurrency();
  const isMobile = useIsMobile();
  const activePolygonIndex = useRef(null);
  const [drawingMode, setDrawingMode] = useState<google.maps.drawing.OverlayType | null>(null);

  const options = useMemo(
    () => ({
      disableDefaultUI: true,
      clickableIcons: false,
      streetViewControl: false,
      zoomControlOptions: {
        position: 3,
      },
      zoomControl: true,
    }),
    [],
  );
  const onLoad = useCallback((map) => (mapRef.current = map), []);

  useEffect(() => {
    if (!isFirstPageLoad) {
      return;
    }
    if (mapRef?.current && (zoomValue || (center?.lat && center?.lng))) {
      setIsFirstPageLoad(false);

      if (zoomValue) {
        mapRef.current.setZoom(Number(zoomValue));
      }

      if (center?.lat && center?.lng) {
        mapRef.current.setCenter({ lat: Number(center?.lat), lng: Number(center?.lng) });
      }
    }
  }, [mapRef?.current, zoomValue, center, isFirstPageLoad]);

  useEffect(() => {
    if (clickedMarkerInfo?.e && clickedMarkerInfo?.activeMarkerData) {
      handleMarkerInfoBox(clickedMarkerInfo);
    }
  }, [clickedMarkerInfo]);

  const handleMarkerClick = ({ e, activeMarkerData }) => {
    e?.domEvent?.stopPropagation();
    e?.domEvent?.preventDefault();
    setActiveMarker(null);
    setClickedMarkerInfo({ e, activeMarkerData });
  };

  const handleMarkerInfoBox = ({ e, activeMarkerData }) => {
    setActiveCluster(null);
    setActiveMarkerPosition(e.latLng);
    setActiveMarker(activeMarkerData);
    setClickedMarkerInfo(null);
  };

  const handleClusterHover = (cluster) => {
    const clusterMarkers = cluster.markers;
    const markerData = clusterMarkers.map((marker) => ({
      lat: marker.position.lat(),
      lng: marker.position.lng(),
    }));
    const markersUnderCluster = listings.filter((loc) =>
      markerData.find((md) => md.lat == loc.Latitude && md.lng == loc.Longitude),
    );
    setActiveCluster({
      count: markersUnderCluster.length,
      ...calculatePriceRange(markersUnderCluster),
    });
    setActiveMarker(null);
    setActiveMarkerPosition(cluster.getCenter());
  };

  const calculatePriceRange = (
    listings: Listing[],
  ): { min: number; max: number; average: number } => {
    if (listings.length === 0) {
      throw new Error('Listings is empty');
    }

    // Get the converted price of each listing
    const prices: number[] = listings.map((listing) => {
      return getListingPrice({
        originatingSystemName: listing.OriginatingSystemName!,
        country: listing.Country,
        listingCurrency: listing.CurrencyCode,
        listPrice: listing.ListPrice!,
        closePrice: listing.ClosePrice,
        standardStatus: listing.StandardStatus,
        closePriceToDisplay: listing.ClosePriceToDisplay,
      }).rawPrice;
    });

    const min = Math.min(...prices);
    const max = Math.max(...prices);
    const total = prices.reduce((acc, price) => acc + price, 0);
    const average = total / prices.length;

    return { min, max, average };
  };

  const svgMarker = {
    path: 'M2,0c0,1.1-0.9,2-2,2s-2-0.9-2-2s0.9-2,2-2S2-1.1,2,0z',
    fillColor: '#E60000',
    fillOpacity: 1,
    strokeColor: '#fff',
    strokeWeight: 3,
    rotation: 0,
    scale: 5,
  };
  const svgMarkerActive = {
    path: 'M2,0c0,1.1-0.9,2-2,2s-2-0.9-2-2s0.9-2,2-2S2-1.1,2,0z',
    fillColor: '#000',
    fillOpacity: 1,
    strokeColor: '#fff',
    strokeWeight: 3,
    rotation: 0,
    scale: 5,
  };

  const updateMapValues = () => {
    const map = mapRef?.current;

    if (map) {
      const currentZoom = map.getZoom();
      const currentCenter = map.getCenter();

      // Update the zoom and center state if they have changed
      if (currentCenter && currentZoom) {
        if (
          currentZoom !== zoomValue ||
          currentCenter.lat().toString() !== center?.lat ||
          currentCenter.lng().toString() !== center?.lng
        ) {
          setZoomValue(currentZoom.toString());
          setCenter({
            lat: currentCenter.lat().toString(),
            lng: currentCenter.lng().toString(),
          });
        }
      }
    }
  };

  const hideTooltipOnMapZoom = () => {
    setActiveMarker(null);
    setActiveCluster(null);
    updateMapValues();
  };

  const onClickPolygon = (index) => {
    activePolygonIndex.current = index;
  };

  const convertPolygonToCoordinates = (polygon: google.maps.Polygon) => {
    const newPolygon = polygon
      .getPath()
      .getArray()
      .map((latLng) => ({ lat: latLng.lat(), lng: latLng.lng() }));

    // Close the polygon
    newPolygon.push(newPolygon[0]!);

    return newPolygon;
  };

  const onPolygonComplete = (polygon: google.maps.Polygon) => {
    setPolygonRef(polygon);
    setDrawingMode(null);

    polygon.addListener('mouseup', () => {
      onPolygonChange(convertPolygonToCoordinates(polygon));
    });

    onPolygonChange(convertPolygonToCoordinates(polygon));
  };

  const onDeleteDrawing = () => {
    google.maps.event.clearInstanceListeners(polygonRef as any);
    polygonRef?.setMap(null);
    setPolygonRef(null);
    onPolygonChange(null);
  };

  const isPointInPolygon = (latitude, longitude, polygon) => {
    if (typeof latitude !== 'number' || typeof longitude !== 'number') {
      throw new TypeError('Invalid latitude or longitude. Numbers are expected');
    } else if (!polygon || !Array.isArray(polygon)) {
      throw new TypeError('Invalid polygon. Array with locations expected');
    } else if (polygon.length === 0) {
      throw new TypeError('Invalid polygon. Non-empty Array expected');
    }

    const x = latitude;
    const y = longitude;

    let inside = false;
    for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
      const xi = polygon[i][0];
      const yi = polygon[i][1];
      const xj = polygon[j][0];
      const yj = polygon[j][1];

      const intersect = yi > y !== yj > y && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi;
      if (intersect) inside = !inside;
    }

    return inside;
  };

  const zoomToPolygon = () => {
    if (mapRef && polygonRef) {
      const bounds = new google.maps.LatLngBounds();
      const points = convertPolygonToCoordinates(polygonRef);
      for (const point of points) {
        bounds?.extend(new google.maps.LatLng(point.lat, point.lng));
      }
      // @ts-ignore
      mapRef.current?.fitBounds(bounds);
    }
  };

  useEffect(() => {
    if (polygonRef) zoomToPolygon();
  }, [polygonRef]);

  useEffect(() => {
    if (isMobile) {
      setPolygonRef(null);
    }
  }, [setCenterAndZoom, isMobile]);

  const onDrawClick = () => {
    if (drawingMode) {
      setDrawingMode(null);
    } else {
      setDrawingMode(window.google.maps.drawing.OverlayType.POLYGON);
    }
  };

  const listingsWithLocation = useMemo(() => {
    return listings.filter((listing) => listing.Location && listing.Latitude && listing.Longitude);
  }, [listings]);

  const centerByMarkersAvg = useMemo(() => {
    let sumLat: number = 0;
    let sumLng: number = 0;
    for (const listing of listingsWithLocation) {
      if (listing.Location) {
        sumLat += listing.Location.lat;
        sumLng += listing.Location.lon;
      }
    }

    const centerLat = sumLat / listingsWithLocation.length;
    const centerLng = sumLng / listingsWithLocation.length;
    return { lat: Number(center?.lat) || centerLat, lng: Number(center?.lng) || centerLng };
  }, [listingsWithLocation]);

  const drawingManagerOptions: google.maps.drawing.DrawingManagerOptions = {
    polygonOptions: {
      fillOpacity: 0.3,
      fillColor: '#bbbafc',
      strokeColor: '#1700ff',
      strokeWeight: 2,
      draggable: true,
      editable: true,
    },
    drawingControl: false,
    drawingControlOptions: {
      position: 3,
      drawingModes: [window?.google?.maps?.drawing?.OverlayType?.POLYGON],
    },
  };

  const onPolygonLoad = () => {
    if (polygonCreatedRef.current) return; // Prevent duplicate creation
    if (!userPolygon || !userPolygon.length) return;

    setDrawingMode(window.google.maps.drawing.OverlayType.POLYGON);

    const polygon = new google.maps.Polygon({
      paths: userPolygon,
      ...drawingManagerOptions.polygonOptions,
    });
    polygon.setMap(mapRef.current);
    setPolygonRef(polygon);
    setDrawingMode(null);

    polygonCreatedRef.current = true;
  };

  // Reset the ref if userPolygon changes or on unmount
  useEffect(() => {
    polygonCreatedRef.current = false;
    return () => {
      polygonCreatedRef.current = false;
    };
  }, [userPolygon]);

  return isLoaded ? (
    <MapWrapper $isMarkerHover={isMarkerHover}>
      {mapPropertyLoader && (
        <OverlayLoader>
          <CircularProgress color="inherit" />
        </OverlayLoader>
      )}
      <GoogleMap
        zoom={setCenterAndZoom ? zoomValue : 0}
        center={centerByMarkersAvg}
        mapContainerClassName="map-container"
        options={options}
        onLoad={onLoad}
        onZoomChanged={hideTooltipOnMapZoom}
        onDragEnd={debounce(() => updateMapValues(), 500)}
      >
        {!isNeighborhoodPage && (
          <DrawingManager
            onLoad={onPolygonLoad}
            drawingMode={drawingMode}
            options={drawingManagerOptions}
            onPolygonComplete={(polygon) => onPolygonComplete(polygon)}
          />
        )}
        {kmlPolygon && kmlPolygon.length > 0 && isNeighborhoodPage && (
          <Polygon
            paths={kmlPolygon}
            options={{
              fillOpacity: 0.3,
              fillColor: '#bbbafc',
              strokeColor: '#1700ff',
              strokeWeight: 2,
              strokeOpacity: 0.8,
            }}
            onLoad={(polygon) => {
              setPolygonRef(polygon);
            }}
          />
        )}

        {!isMobile && !isNeighborhoodPage && (
          <>
            {polygonRef ? (
              <DeleteIconContainer onClick={onDeleteDrawing}>
                <Trash2 />
              </DeleteIconContainer>
            ) : (
              <DrawAreaButton onClick={onDrawClick}>
                {t(
                  drawingMode === window.google.maps.drawing.OverlayType.POLYGON
                    ? 'Cancel'
                    : 'Draw Area',
                )}
              </DrawAreaButton>
            )}
          </>
        )}
        <>
          {listingsWithLocation && (
            <>
              <MarkerClusterer
                clusterClass="cluster"
                onMouseOver={handleClusterHover}
                onMouseOut={() => setActiveMarkerPosition(null)}
                onClick={() => setActiveCluster(null)}
              >
                {(clusterer) => (
                  <>
                    {listingsWithLocation.map((mr, ind) => {
                      return (
                        <Marker
                          key={ind}
                          icon={
                            activeMarker?.ListingId === mr?.ListingId ? svgMarkerActive : svgMarker
                          }
                          position={{ lat: Number(mr.Latitude), lng: Number(mr.Longitude) }}
                          clusterer={clusterer}
                          onClick={(e) => handleMarkerClick({ e, activeMarkerData: mr })}
                          onMouseOver={
                            isMarkerHover
                              ? (e) => handleMarkerClick({ e, activeMarkerData: mr })
                              : () => {}
                          }
                          onMouseOut={isMarkerHover ? (e) => setActiveMarker(null) : () => {}}
                        />
                      );
                    })}
                  </>
                )}
              </MarkerClusterer>

              {activeMarkerPosition && (
                <InfoBox
                  position={activeMarkerPosition}
                  zIndex={999}
                  options={{
                    boxStyle: {
                      width: isMobile ? '100%' : '408px',
                      // pixelOffset: true,
                    } as unknown as CSSStyleDeclaration,
                    closeBoxURL: ``,
                    pixelOffset: activeMarker
                      ? new google.maps.Size(-180, 15)
                      : new google.maps.Size(-180, 40),
                  }}
                >
                  <ClickAwayListener onClickAway={() => setActiveMarkerPosition(null)}>
                    <StyledWrapper>
                      {activeMarker && <ElasticListingMarker listing={activeMarker} />}
                      {activeCluster && (
                        <StyledInfoBoxWrapper>
                          <Headline>
                            {t('Click to view')} {activeCluster.count} {t('homes in this area')}
                          </Headline>
                          <InfoBoxDescription>
                            {t('Homes here are priced between')}{' '}
                            <strong>
                              {activeCluster.min} - {activeCluster.max}
                            </strong>
                            , {t('with an average list price of')}{' '}
                            <strong>{activeCluster.average}</strong>.
                          </InfoBoxDescription>
                        </StyledInfoBoxWrapper>
                      )}
                    </StyledWrapper>
                  </ClickAwayListener>
                </InfoBox>
              )}
            </>
          )}
        </>
      </GoogleMap>
    </MapWrapper>
  ) : (
    <></>
  );
};

export default PropertyListingsMap;
